import React, { useEffect, useMemo, useState } from 'react';
import {
	Alert,
	Button,
	Checkbox,
	Divider,
	Drawer,
	Input,
	Popconfirm,
	Popover,
	Radio,
	Select,
	Switch,
	Table,
	Tag,
	Tooltip,
	Typography,
	Upload,
	message,
} from 'antd';
import {
	InboxOutlined,
	CheckCircleFilled,
	FileTextOutlined,
	SettingOutlined,
	PlusOutlined,
	CloseOutlined,
	ExperimentOutlined,
	SaveOutlined,
} from '@ant-design/icons';
import { usePapaParse } from 'react-papaparse';
import { lambda } from 'src/_shared/services/utils.js';
import gql from 'graphql-tag';
import { queryDepartmentsByCompanyIdIndex } from 'src/_shared/api/graphql/custom/departments/queryDepartmentsByCompanyIdGraphql.js';
import { querySubCompanyByCompanyIdIndex } from 'src/_shared/api/graphql/custom/sub-company/subCompanyByIdGraphql.js';
import { queryUserGroupsByCompanyIdIndex } from 'src/_shared/api/graphql/custom/userGroups/queryUserGroupsByCompanyIdGraphql.js';
import { ListCompanies } from 'src/_shared/api/graphql/custom/company-invite/listCompanyGraphql.js';
import {
	ASSIGNABLE_FIELDS,
	IMPORTANT_FIELDS,
	RULE_TYPES,
	TRANSFORM_OPTIONS,
	autoDetectMappings,
	buildMappingDoc,
	consumedColumns,
	detectFieldMappings,
	fieldLabel,
	interpretStatusValues,
	resolveFieldValue,
	seedFieldSpecs,
	suggestFieldByValues,
	toReferenceSet,
} from './employeeMappingCatalog.js';

const { Dragger } = Upload;
const { Text, Title } = Typography;
const { Option } = Select;

// Fields shown as rows in the field-oriented table (status has its own section).
const FIELD_ROWS = ASSIGNABLE_FIELDS.filter((f) => f.value !== 'status');
const CORE_FIELD_VALUES = FIELD_ROWS.filter((f) => f.core).map((f) => f.value);

const ruleSummary = (spec) => {
	if (!spec) return '';
	switch (spec.type) {
		case 'column':
			return spec.column ? `${spec.column}${spec.transforms?.length ? ` · ${spec.transforms.join('·')}` : ''}` : '';
		case 'coalesce':
			return `coalesce: ${(spec.columns || []).filter(Boolean).join(' → ') || '(none)'}`;
		case 'template':
			return `template: ${spec.template || '(empty)'}`;
		case 'equalsMap':
			return `value map on ${spec.column || '(?)'}`;
		case 'constant':
			return `constant: "${spec.constant}"`;
		case 'none':
			return 'off — disabled';
		default:
			return '';
	}
};

/** Per-field rule editor (rendered inside a Popover). */
const RuleEditor = ({ spec, headers, distinctByColumn, onChange }) => {
	const s = spec || { type: 'column', column: '', transforms: [] };
	const patch = (delta) => onChange({ ...s, ...delta });

	const changeType = (type) => {
		const carriedColumn = s.column || (s.columns && s.columns[0]) || '';
		const fresh = {
			column: { type: 'column', column: carriedColumn, transforms: [] },
			coalesce: { type: 'coalesce', columns: carriedColumn ? [carriedColumn] : [] },
			template: { type: 'template', template: carriedColumn ? `{${carriedColumn}}` : '', requireAll: false },
			equalsMap: { type: 'equalsMap', column: carriedColumn, transform: '', cases: [{ value: '', result: '' }], default: '' },
			constant: { type: 'constant', constant: '' },
			none: { type: 'none' },
		}[type];
		onChange(fresh);
	};

	const columnSelect = (value, onPick, extra = {}) => (
		<Select
			showSearch
			size="small"
			placeholder="Choose a column"
			style={{ width: '100%' }}
			value={value || undefined}
			onChange={onPick}
			filterOption={(input, option) => (option?.value || '').toLowerCase().includes(input.toLowerCase())}
			{...extra}
		>
			{headers.map((h) => (
				<Option key={h} value={h}>
					{h}
				</Option>
			))}
		</Select>
	);

	return (
		<div style={{ width: 380 }}>
			<Text type="secondary" style={{ fontSize: 12 }}>
				Rule type
			</Text>
			<Select size="small" style={{ width: '100%', marginBottom: 10 }} value={s.type} onChange={changeType}>
				{RULE_TYPES.map((r) => (
					<Option key={r.value} value={r.value}>
						{r.label}
					</Option>
				))}
			</Select>

			{s.type === 'column' ? (
				<>
					{columnSelect(s.column, (col) => patch({ column: col }))}
					<Text type="secondary" style={{ fontSize: 12, display: 'block', margin: '8px 0 2px' }}>
						Transforms (applied in order)
					</Text>
					<Select
						size="small"
						mode="multiple"
						allowClear
						placeholder="none"
						style={{ width: '100%' }}
						value={s.transforms || []}
						onChange={(transforms) => patch({ transforms })}
						options={TRANSFORM_OPTIONS}
					/>
				</>
			) : null}

			{s.type === 'coalesce' ? (
				<>
					<Text type="secondary" style={{ fontSize: 12 }}>
						Columns in priority order (first non-empty wins)
					</Text>
					<Select
						showSearch
						size="small"
						mode="multiple"
						placeholder="Add columns…"
						style={{ width: '100%' }}
						value={s.columns || []}
						onChange={(columns) => patch({ columns })}
						filterOption={(input, option) => (option?.value || '').toLowerCase().includes(input.toLowerCase())}
					>
						{headers.map((h) => (
							<Option key={h} value={h}>
								{h}
							</Option>
						))}
					</Select>
				</>
			) : null}

			{s.type === 'template' ? (
				<>
					<Text type="secondary" style={{ fontSize: 12 }}>
						Use {'{Column|transform}'} placeholders, e.g. {'{Last Name|lowerTrim|first4}{SSN}'}
					</Text>
					<Input.TextArea
						size="small"
						autoSize={{ minRows: 2, maxRows: 4 }}
						value={s.template}
						onChange={(e) => patch({ template: e.target.value })}
					/>
					<Checkbox
						checked={Boolean(s.requireAll)}
						onChange={(e) => patch({ requireAll: e.target.checked })}
						style={{ marginTop: 6, fontSize: 12 }}
					>
						Require every placeholder (blank if any is empty)
					</Checkbox>
				</>
			) : null}

			{s.type === 'equalsMap' ? (
				<>
					<Text type="secondary" style={{ fontSize: 12 }}>
						Column
					</Text>
					{columnSelect(s.column, (col) => patch({ column: col }))}
					<Text type="secondary" style={{ fontSize: 12, display: 'block', margin: '8px 0 2px' }}>
						Value → result
					</Text>
					{(s.cases || []).map((c, index) => (
						<div key={index} style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
							<Select
								showSearch
								size="small"
								placeholder="value"
								style={{ width: 130 }}
								value={c.value || undefined}
								onChange={(value) => {
									const cases = [...s.cases];
									cases[index] = { ...cases[index], value };
									patch({ cases });
								}}
								options={(distinctByColumn[s.column] || []).map((d) => ({ value: d.value, label: `${d.value} (${d.count})` }))}
							/>
							<Input
								size="small"
								placeholder="result"
								style={{ width: 130 }}
								value={c.result}
								onChange={(e) => {
									const cases = [...s.cases];
									cases[index] = { ...cases[index], result: e.target.value };
									patch({ cases });
								}}
							/>
							<Button
								size="small"
								type="text"
								icon={<CloseOutlined />}
								onClick={() => patch({ cases: s.cases.filter((_, i) => i !== index) })}
							/>
						</div>
					))}
					<Button
						size="small"
						type="link"
						icon={<PlusOutlined />}
						onClick={() => patch({ cases: [...(s.cases || []), { value: '', result: '' }] })}
					>
						Add case
					</Button>
					<Text type="secondary" style={{ fontSize: 12, display: 'block', margin: '6px 0 2px' }}>
						Default (when no case matches)
					</Text>
					<Input size="small" value={s.default} onChange={(e) => patch({ default: e.target.value })} />
				</>
			) : null}

			{s.type === 'constant' ? (
				<Input
					size="small"
					placeholder="Value for every row"
					value={s.constant}
					onChange={(e) => patch({ constant: e.target.value })}
				/>
			) : null}

			{s.type === 'none' ? (
				<Text type="secondary" style={{ fontSize: 12 }}>
					This field is disabled — no value is written from the feed.
				</Text>
			) : null}
		</div>
	);
};

/**
 * AEF setup wizard (prototype), field-oriented. Drop a sample CSV → each ERIN
 * field auto-fills from the pipeline defaults; edit any field's rule (column,
 * coalesce, template, value map, constant, off) + transforms; leftover columns
 * become custom values. Generates the per-company mapping doc the pipeline reads.
 */
const MAPPING_CONFIG_ENDPOINT = 'mapping-config-api';

const AefMappingWizard = ({
	visible,
	onClose,
	client,
	onDryRun,
	updatedByEmail,
	companies = [],
	defaultCompanyId = '',
	defaultCompanyName = '',
}) => {
	const { readString } = usePapaParse();
	const [companyId, setCompanyId] = useState(defaultCompanyId);
	const [fileName, setFileName] = useState('');
	const [headers, setHeaders] = useState([]);
	const [rows, setRows] = useState([]);
	const [fieldSpecs, setFieldSpecs] = useState({}); // field -> ui rule spec
	const [suggested, setSuggested] = useState(new Set());
	const [addedFields, setAddedFields] = useState(new Set()); // optional fields shown by choice
	const [absentClaimAction, setAbsentClaimAction] = useState('disable');
	const [statusColumn, setStatusColumn] = useState('');
	const [statusInactive, setStatusInactive] = useState(new Set());
	const [note, setNote] = useState('');
	const [refData, setRefData] = useState(null);
	const [allCompanies, setAllCompanies] = useState([]); // full directory (lazy)
	const [busy, setBusy] = useState(false);
	const [validation, setValidation] = useState(null); // { ok, error?, fields?, policy? }

	const detection = useMemo(() => autoDetectMappings(headers), [headers]);
	const autoFieldColumns = useMemo(() => detectFieldMappings(headers), [headers]);
	const hasFile = headers.length > 0;

	// Selector options: the full company directory once loaded, else the ledger-
	// derived list; the active company is always included and pre-selected.
	const companyOptions = useMemo(() => {
		const base = allCompanies.length ? allCompanies : companies;
		const byId = new Map();
		for (const c of base) if (c?.id) byId.set(c.id, c.name || c.id);
		if (defaultCompanyId && !byId.has(defaultCompanyId)) {
			byId.set(defaultCompanyId, defaultCompanyName || 'Selected company');
		}
		return [...byId.entries()]
			.map(([id, name]) => ({ id, name }))
			.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
	}, [allCompanies, companies, defaultCompanyId, defaultCompanyName]);

	// Lazy-load the full company directory the first time the wizard opens.
	useEffect(() => {
		if (!visible || !client || allCompanies.length) return undefined;
		let cancelled = false;
		(async () => {
			try {
				const res = await client.query({
					query: ListCompanies,
					variables: { limit: 1000 },
					fetchPolicy: 'cache-first',
				});
				const items = res?.data?.listCompanies?.items || [];
				if (!cancelled) {
					// Exclude disabled sites — they shouldn't get new integrations.
					setAllCompanies(
						items
							.filter((c) => c?.id && c?.name && !c.disableSite)
							.map((c) => ({ id: c.id, name: c.name }))
					);
				}
			} catch (e) {
				// keep the ledger-derived fallback list
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [visible, client, allCompanies.length]);

	// Company reference data for value-based suggestions on leftover columns.
	useEffect(() => {
		if (!visible || !client || !companyId || companyId.length < 8) {
			setRefData(null);
			return undefined;
		}
		let cancelled = false;
		(async () => {
			const fetchItems = async (query, root) => {
				try {
					const res = await client.query({
						query: gql(query),
						variables: { companyId, first: 1000 },
						fetchPolicy: 'cache-first',
					});
					return res?.data?.[root]?.items || [];
				} catch (e) {
					return [];
				}
			};
			const [depts, subs, groups] = await Promise.all([
				fetchItems(queryDepartmentsByCompanyIdIndex, 'queryDepartmentsByCompanyIdIndex'),
				fetchItems(querySubCompanyByCompanyIdIndex, 'querySubCompanyByCompanyIdIndex'),
				fetchItems(queryUserGroupsByCompanyIdIndex, 'queryUserGroupsByCompanyIdIndex'),
			]);
			if (cancelled) return;
			setRefData({
				departments: toReferenceSet(depts),
				subCompanies: toReferenceSet(subs),
				userGroups: toReferenceSet(groups),
			});
		})();
		return () => {
			cancelled = true;
		};
	}, [visible, client, companyId]);

	const handleFile = async (file) => {
		try {
			const text = await file.text();
			readString(text, {
				header: true,
				skipEmptyLines: true,
				transformHeader: (h) => (h || '').trim(),
				complete: (results) => {
					const parsedHeaders = (results?.meta?.fields || []).filter(Boolean);
					if (!parsedHeaders.length) {
						message.error('No header row found in that file.');
						return;
					}
					const det = autoDetectMappings(parsedHeaders);
					const seed = seedFieldSpecs(det.columns);
					const parsedRows = results.data || [];
					const statusCol = det.statusColumn || '';
					const inactiveInit = new Set();
					if (statusCol) {
						const distinct = [
							...new Set(parsedRows.map((r) => (r[statusCol] ?? '').toString().trim()).filter(Boolean)),
						];
						for (const st of interpretStatusValues(statusCol, distinct)) if (!st.active) inactiveInit.add(st.value);
					}
					setFileName(file.name);
					setHeaders(parsedHeaders);
					setRows(parsedRows);
					setFieldSpecs(seed.fieldSpecs);
					setSuggested(seed.suggested);
					setAddedFields(new Set());
					setStatusColumn(statusCol);
					setStatusInactive(inactiveInit);
				},
			});
		} catch (error) {
			console.error('Failed to read sample file:', error);
			message.error('Could not read that file — is it a CSV?');
		}
		return false;
	};

	const distinctByColumn = useMemo(() => {
		const counts = {};
		for (const h of headers) counts[h] = new Map();
		for (const row of rows) {
			for (const h of headers) {
				const v = (row[h] ?? '').toString().trim();
				if (v) counts[h].set(v, (counts[h].get(v) || 0) + 1);
			}
		}
		const result = {};
		for (const h of headers) {
			result[h] = [...counts[h].entries()].sort((a, b) => b[1] - a[1]).map(([value, count]) => ({ value, count }));
		}
		return result;
	}, [headers, rows]);

	const setFieldSpec = (field, spec) => {
		setFieldSpecs((prev) => {
			const next = { ...prev };
			if (spec) next[field] = spec;
			else delete next[field];
			return next;
		});
	};

	const addField = (field) => {
		setAddedFields((prev) => new Set(prev).add(field));
		setFieldSpec(field, { type: 'column', column: '', transforms: [] });
	};

	const promoteColumn = (column, field) => {
		if (!CORE_FIELD_VALUES.includes(field)) setAddedFields((prev) => new Set(prev).add(field));
		setFieldSpec(field, { type: 'column', column, transforms: [] });
	};

	// Which field rows to show: core + any field with a rule + user-added.
	const visibleFields = useMemo(() => {
		const set = new Set(CORE_FIELD_VALUES);
		for (const f of Object.keys(fieldSpecs)) set.add(f);
		for (const f of addedFields) set.add(f);
		return FIELD_ROWS.map((f) => f.value).filter((f) => set.has(f));
	}, [fieldSpecs, addedFields]);

	const addableFields = FIELD_ROWS.filter((f) => !visibleFields.includes(f.value));

	// Leftover columns (not consumed by a field rule, not the status column) → customs.
	const leftoverColumns = useMemo(() => {
		const consumed = consumedColumns(fieldSpecs);
		return headers.filter((h) => !consumed.has(h) && h !== statusColumn);
	}, [headers, fieldSpecs, statusColumn]);

	const valueSuggestions = useMemo(() => {
		if (!refData) return {};
		const out = {};
		for (const column of leftoverColumns) {
			const distinct = (distinctByColumn[column] || []).map((d) => d.value);
			const suggestion = suggestFieldByValues(distinct, refData);
			if (suggestion) out[column] = suggestion;
		}
		return out;
	}, [refData, leftoverColumns, distinctByColumn]);

	const missingImportant = IMPORTANT_FIELDS.filter((f) => !fieldSpecs[f]);

	const statusDistinct = useMemo(
		() => (statusColumn ? distinctByColumn[statusColumn] || [] : []),
		[statusColumn, distinctByColumn]
	);

	const changeStatusColumn = (value) => {
		const next = value === '__none__' ? '' : value;
		setStatusColumn(next);
		const inactive = new Set();
		if (next) {
			const distinct = (distinctByColumn[next] || []).map((d) => d.value);
			for (const s of interpretStatusValues(next, distinct)) if (!s.active) inactive.add(s.value);
		}
		setStatusInactive(inactive);
	};

	const statusRules = useMemo(() => {
		if (!statusColumn) return [];
		return [{ field: statusColumn, inactiveValues: [...statusInactive] }];
	}, [statusColumn, statusInactive]);

	const doc = useMemo(
		() =>
			buildMappingDoc({
				companyId: companyId || '<companyId>',
				fieldSpecs,
				autoFieldColumns,
				absentClaimAction,
				statusRules,
				note: note || undefined,
			}),
		[companyId, fieldSpecs, autoFieldColumns, absentClaimAction, statusRules, note]
	);

	const docJson = JSON.stringify(doc, null, 2);

	const copyDoc = async () => {
		try {
			await navigator.clipboard.writeText(docJson);
			message.success('Mapping JSON copied.');
		} catch (e) {
			message.error('Copy failed — select the text manually.');
		}
	};

	const hasCompany = companyId && !companyId.startsWith('<');

	const callConfigApi = async (variables) => {
		const res = await lambda({ endpoint: MAPPING_CONFIG_ENDPOINT, variables });
		const raw = res?.data ?? res;
		const payload = typeof raw === 'string' ? JSON.parse(raw) : raw;
		if (payload?.ok === false) throw new Error(payload.error);
		return payload;
	};

	// Server-side validate with the SAME compiler the workers run — a doc that
	// validates is a doc that runs.
	const validateDoc = async () => {
		setBusy(true);
		try {
			const result = await callConfigApi({ action: 'validate', doc });
			setValidation({ ok: true, fields: result.fields, policy: result.policy });
			return true;
		} catch (e) {
			setValidation({ ok: false, error: e?.message || 'validation failed' });
			return false;
		} finally {
			setBusy(false);
		}
	};

	const saveDoc = async () => {
		if (!hasCompany) {
			message.error('Pick a company first.');
			return;
		}
		if (!(await validateDoc())) return;
		setBusy(true);
		try {
			await callConfigApi({ action: 'upsert', doc, updatedByEmail });
			message.success('Mapping config saved — the next run uses it.');
		} catch (e) {
			message.error(`Save failed: ${e?.message || 'unknown error'}`);
		} finally {
			setBusy(false);
		}
	};

	// Dry-run the UNSAVED doc against the company's latest landed file (test before
	// save). Delegated to the parent, which owns the processing ledger + trigger.
	const dryRunDoc = async () => {
		if (!hasCompany) {
			message.error('Pick a company first.');
			return;
		}
		if (!(await validateDoc())) return;
		if (onDryRun) onDryRun({ companyId, taskType: 'employee', doc });
	};

	const fieldColumns = [
		{
			title: 'ERIN field',
			key: 'field',
			width: 200,
			render: (field) => (
				<span>
					<Text strong>{fieldLabel(field)}</Text>
					{suggested.has(field) ? (
						<Tooltip title="Auto-filled from a common non-default header">
							<Tag color="gold" style={{ marginLeft: 6 }}>
								suggested
							</Tag>
						</Tooltip>
					) : null}
					{IMPORTANT_FIELDS.includes(field) && !fieldSpecs[field] ? (
						<Tag color="red" style={{ marginLeft: 6 }}>
							needs a column
						</Tag>
					) : null}
				</span>
			),
		},
		{
			title: 'Rule',
			key: 'rule',
			render: (field) => {
				const spec = fieldSpecs[field];
				const editor = (
					<Popover
						trigger="click"
						placement="left"
						title={`Rule — ${fieldLabel(field)}`}
						content={
							<RuleEditor
								spec={spec}
								headers={headers}
								distinctByColumn={distinctByColumn}
								onChange={(next) => setFieldSpec(field, next)}
							/>
						}
					>
						<Button size="small" icon={<SettingOutlined />} />
					</Popover>
				);
				if (!spec || spec.type === 'column') {
					return (
						<div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
							<Select
								showSearch
								allowClear
								size="small"
								style={{ width: 200 }}
								placeholder="— unmapped —"
								value={spec?.column || undefined}
								onChange={(col) =>
									setFieldSpec(field, col ? { type: 'column', column: col, transforms: spec?.transforms || [] } : null)
								}
								filterOption={(input, option) => (option?.value || '').toLowerCase().includes(input.toLowerCase())}
							>
								{headers.map((h) => (
									<Option key={h} value={h}>
										{h}
									</Option>
								))}
							</Select>
							{spec?.transforms?.length ? <Tag color="geekblue">{spec.transforms.join('·')}</Tag> : null}
							{editor}
						</div>
					);
				}
				return (
					<div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
						<Tag color="purple">{ruleSummary(spec)}</Tag>
						{editor}
					</div>
				);
			},
		},
		{
			title: 'Sample',
			key: 'sample',
			width: 150,
			render: (field) => (
				<Text type="secondary" style={{ fontSize: 12 }} ellipsis>
					{rows[0] ? resolveFieldValue(fieldSpecs[field], rows[0]) : ''}
				</Text>
			),
		},
		{
			title: '',
			key: 'clear',
			width: 36,
			render: (field) =>
				fieldSpecs[field] ? (
					<Tooltip title="Clear this field">
						<Button
							size="small"
							type="text"
							icon={<CloseOutlined />}
							onClick={() => {
								setFieldSpec(field, null);
								setAddedFields((prev) => {
									const next = new Set(prev);
									next.delete(field);
									return next;
								});
							}}
						/>
					</Tooltip>
				) : null,
		},
	];

	// Preview: every field with a resolvable rule, in row order.
	const previewFields = visibleFields.filter((f) => {
		const s = fieldSpecs[f];
		return s && s.type !== 'none';
	});
	const previewRows = rows.slice(0, 8).map((row, index) => {
		const mapped = { key: index };
		for (const field of previewFields) mapped[field] = resolveFieldValue(fieldSpecs[field], row);
		return mapped;
	});
	const previewColumns = previewFields.map((field) => ({
		title: fieldLabel(field),
		dataIndex: field,
		key: field,
		render: (value) => <Text style={{ fontSize: 12 }}>{value ?? ''}</Text>,
	}));

	const refLabel = (refKey) =>
		refKey === 'subCompanies' ? 'sub-companies' : refKey === 'userGroups' ? 'user groups' : 'departments';

	return (
		<Drawer
			title="Set up an employee feed from a sample file"
			width={980}
			visible={visible}
			onClose={onClose}
			footer={
				<div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
					<Button onClick={onClose}>Close</Button>
					<div style={{ display: 'flex', gap: 8 }}>
						<Button disabled={!hasFile} onClick={copyDoc}>
							Copy JSON
						</Button>
						<Button icon={<ExperimentOutlined />} loading={busy} disabled={!hasFile} onClick={dryRunDoc}>
							Dry-run
						</Button>
						<Popconfirm
							title="Save this config for the selected company? The next run uses it for real writes."
							okText="Save"
							onConfirm={saveDoc}
						>
							<Button type="primary" icon={<SaveOutlined />} loading={busy} disabled={!hasFile}>
								Save config
							</Button>
						</Popconfirm>
					</div>
				</div>
			}
		>
			<div style={{ marginBottom: 16 }}>
				<Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
					Company
				</Text>
				<Select
					showSearch
					style={{ width: 440 }}
					placeholder="Search company by name or ID"
					value={companyId || undefined}
					optionLabelProp="label"
					filterOption={(input, option) => (option?.searchtext || '').includes(input.toLowerCase())}
					onChange={setCompanyId}
				>
					{companyOptions.map((c) => (
						<Option key={c.id} value={c.id} label={c.name} searchtext={`${c.name} ${c.id}`.toLowerCase()}>
							<div>
								<Text>{c.name}</Text>
								<Text type="secondary" style={{ fontSize: 11, display: 'block' }}>
									{c.id}
								</Text>
							</div>
						</Option>
					))}
				</Select>
			</div>

			{hasFile ? (
				<div
					style={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						padding: '8px 12px',
						background: '#f6ffed',
						border: '1px solid #b7eb8f',
						borderRadius: 8,
						marginBottom: 16,
					}}
				>
					<Text>
						<FileTextOutlined /> {fileName} · {rows.length.toLocaleString()} rows · {headers.length} columns
					</Text>
					<Upload accept=".csv,text/csv" beforeUpload={handleFile} showUploadList={false} maxCount={1}>
						<Button size="small">Replace file</Button>
					</Upload>
				</div>
			) : (
				<Dragger accept=".csv,text/csv" beforeUpload={handleFile} showUploadList={false} maxCount={1} style={{ marginBottom: 16 }}>
					<p style={{ margin: '10px 0' }}>
						<InboxOutlined style={{ fontSize: 22, color: '#1890ff', marginRight: 8 }} />
						<Text>Drop a sample employee CSV here, or click to browse</Text>
					</p>
				</Dragger>
			)}

			{hasFile ? (
				<>
					{missingImportant.length ? (
						<Alert
							showIcon
							type="warning"
							style={{ marginBottom: 12 }}
							message={`Needs a column: ${missingImportant.map(fieldLabel).join(', ')}`}
							description="An account claim needs a name, a contact email, and a stable match key. Assign a column, or confirm the feed omits these."
						/>
					) : null}

					<Title level={5}>ERIN fields</Title>
					<Text type="secondary" style={{ display: 'block', marginBottom: 8, fontSize: 12 }}>
						Each field auto-filled from the standard mappings. Change the column, or open the gear to build a rule
						(coalesce, template, value map, transforms, constant, or turn it off).
					</Text>
					<Table rowKey={(field) => field} size="small" pagination={false} columns={fieldColumns} dataSource={visibleFields} />
					{addableFields.length ? (
						<div style={{ marginTop: 8 }}>
							<Select
								showSearch
								size="small"
								style={{ width: 240 }}
								placeholder="+ add another field"
								value={undefined}
								onChange={addField}
								filterOption={(input, option) => (option?.children || '').toLowerCase?.().includes(input.toLowerCase())}
							>
								{addableFields.map((f) => (
									<Option key={f.value} value={f.value}>
										{f.label}
									</Option>
								))}
							</Select>
						</div>
					) : null}

					<Divider />
					<Title level={5}>Custom values ({leftoverColumns.length})</Title>
					<Text type="secondary" style={{ display: 'block', marginBottom: 8, fontSize: 12 }}>
						Every column not used by a field above is stored as a custom value automatically. Promote any that
						should be a real field.
					</Text>
					<div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
						{leftoverColumns.length === 0 ? <Text type="secondary">— none —</Text> : null}
						{leftoverColumns.map((column) => {
							const suggestion = valueSuggestions[column];
							const dist = distinctByColumn[column] || [];
							const sample = dist[0]?.value ?? '';
							return (
								<div
									key={column}
									style={{
										border: '1px solid #f0f0f0',
										borderRadius: 6,
										padding: '6px 10px',
										display: 'flex',
										flexDirection: 'column',
										gap: 2,
										maxWidth: 240,
									}}
								>
									<div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
										<Text strong style={{ fontSize: 12 }} ellipsis>
											{column}
										</Text>
										{suggestion ? (
											<Tooltip
												title={`${suggestion.matched} of ${suggestion.total} values match this company's stored ${refLabel(
													suggestion.refKey
												)} — click to map`}
											>
												<Tag
													color="purple"
													style={{ cursor: 'pointer', margin: 0 }}
													onClick={() => promoteColumn(column, suggestion.field)}
												>
													→ {fieldLabel(suggestion.field)} · {suggestion.matched}/{suggestion.total}
												</Tag>
											</Tooltip>
										) : (
											<Popover
												trigger="click"
												title={`Promote "${column}"`}
												content={
													<Select
														showSearch
														size="small"
														style={{ width: 220 }}
														placeholder="Map to field…"
														onChange={(field) => promoteColumn(column, field)}
													>
														{FIELD_ROWS.map((f) => (
															<Option key={f.value} value={f.value}>
																{f.label}
															</Option>
														))}
													</Select>
												}
											>
												<Button size="small" type="link" style={{ padding: 0 }}>
													promote
												</Button>
											</Popover>
										)}
									</div>
									{sample ? (
										<Tooltip title={dist.slice(0, 8).map((d) => `${d.value} (${d.count})`).join(', ')}>
											<Text type="secondary" style={{ fontSize: 11 }} ellipsis>
												e.g. {sample}
												{dist.length > 1 ? ` · ${dist.length} distinct` : ''}
											</Text>
										</Tooltip>
									) : null}
								</div>
							);
						})}
					</div>

					<Divider />
					<Title level={5}>Is this a full roster or changes-only?</Title>
					<Radio.Group value={absentClaimAction} onChange={(e) => setAbsentClaimAction(e.target.value)}>
						<Radio value="disable">
							Full roster — anyone <b>absent</b> from the file is treated as terminated
						</Radio>
						<br />
						<Radio value="skip">Changes only (delta) — absent employees are left unchanged</Radio>
					</Radio.Group>
					<Alert
						type={absentClaimAction === 'disable' ? 'warning' : 'info'}
						showIcon
						style={{ marginTop: 8 }}
						message={
							absentClaimAction === 'disable'
								? 'Full-roster: a short/truncated file would disable everyone missing — the pipeline’s 25% breaker is the backstop. Confirm each drop is the complete roster.'
								: 'Delta: absent employees are never auto-disabled from this feed.'
						}
					/>

					<Divider />
					<Title level={5}>Employment status</Title>
					<Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 6 }}>
						Which column marks an employee active or inactive? Choose “Don’t map status” to treat everyone in
						the file as active.
					</Text>
					<Select size="small" style={{ width: 320 }} value={statusColumn || '__none__'} onChange={changeStatusColumn}>
						<Option value="__none__">Don’t map status (everyone active)</Option>
						{headers.map((h) => (
							<Option key={h} value={h}>
								{h}
							</Option>
						))}
					</Select>
					{statusColumn ? (
						<div style={{ marginTop: 10 }}>
							<Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
								Mark each value active or inactive:
							</Text>
							{statusDistinct.map(({ value, count }) => {
								const inactive = statusInactive.has(value);
								return (
									<div key={value} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
										<Switch
											size="small"
											checked={!inactive}
											onChange={(checked) =>
												setStatusInactive((prev) => {
													const next = new Set(prev);
													if (checked) next.delete(value);
													else next.add(value);
													return next;
												})
											}
										/>
										<Text>{value}</Text>
										<Tag color={inactive ? 'red' : 'green'}>{inactive ? 'inactive' : 'active'}</Tag>
										<Text type="secondary" style={{ fontSize: 11 }}>
											({count.toLocaleString()})
										</Text>
									</div>
								);
							})}
							<Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 4 }}>
								Any value not in the sample defaults to active.
							</Text>
						</div>
					) : (
						<Alert
							type="info"
							showIcon
							style={{ marginTop: 8 }}
							message="Status not mapped — every employee in the file is treated as active (absent employees are still handled by the roster setting above)."
						/>
					)}

					{previewColumns.length ? (
						<>
							<Divider />
							<Title level={5}>Preview (first 8 rows, as mapped)</Title>
							<div style={{ overflowX: 'auto' }}>
								<Table rowKey="key" size="small" pagination={false} columns={previewColumns} dataSource={previewRows} />
							</div>
						</>
					) : null}

					<Divider />
					<Title level={5}>Mapping document</Title>
					<Input.TextArea
						placeholder="Optional note (source system, quirks, validation date)…"
						value={note}
						onChange={(e) => setNote(e.target.value)}
						autoSize={{ minRows: 1, maxRows: 3 }}
						style={{ marginBottom: 8 }}
					/>
					<pre
						style={{
							background: '#0f172a',
							color: '#e2e8f0',
							padding: 14,
							borderRadius: 8,
							fontSize: 12,
							overflowX: 'auto',
						}}
					>
						{docJson}
					</pre>
					{validation ? (
						validation.ok ? (
							<Alert
								type="success"
								showIcon
								style={{ marginTop: 12 }}
								message="Config compiles clean (validated by the pipeline)"
								description={
									<span>
										Field overrides:{' '}
										{validation.fields?.length ? (
											validation.fields.map((f) => <Tag key={f}>{f}</Tag>)
										) : (
											<Text type="secondary">none (defaults)</Text>
										)}
										{' · policy: '}
										{validation.policy?.length ? (
											validation.policy.map((p) => (
												<Tag key={p} color="purple">
													{p}
												</Tag>
											))
										) : (
											<Text type="secondary">none</Text>
										)}
									</span>
								}
							/>
						) : (
							<Alert type="error" showIcon style={{ marginTop: 12 }} message={validation.error} />
						)
					) : null}
				</>
			) : null}
		</Drawer>
	);
};

export default AefMappingWizard;
